import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import { DatePicker, MaskedInput, Select, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useState } from "react";
import type { FieldErrors } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { components } from "react-select";
import { useGetDocumentsQuery } from "services/documents-api";
import type { PayHistoryPayload, PayHistoryRow } from "types/DataTypes";

import type { OptionProps, SingleValueProps } from "react-select";

type PayHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PayHistoryPayload) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  initialData?: PayHistoryRow;
  title: string;
  service: { file: { id: number }; id: number; type: string };
};

type StatusOptionType = {
  label: string;
  value: "PRESENT_AND_PAID" | "PRESENT_NOT_PAID" | "MISSING" | "REQUESTED";
  color: "green" | "purple" | "yellow";
};

type DocumentOption = {
  label: string;
  value: number;
};

const DocumentOption = (props: OptionProps<DocumentOption>) => (
  <components.Option {...props}>
    <div className="flex items-center gap-2">
      <span>{props.data.label}</span>
    </div>
  </components.Option>
);

const DocumentSingleValue = (props: SingleValueProps<DocumentOption>) => (
  <components.SingleValue {...props}>
    <div className="flex items-center gap-2">
      <span>{props.data.label}</span>
    </div>
  </components.SingleValue>
);

const StatusOption = (props: OptionProps<StatusOptionType>) => (
  <components.Option {...props}>
    <div className="flex items-center gap-2">
      <div
        className={props.data.color}
        style={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          backgroundColor: props.data.color,
          flexShrink: 0,
          marginRight: "8px",
        }}
      />
      <span>{props.data.label}</span>
    </div>
  </components.Option>
);

const StatusSingleValue = (props: SingleValueProps<StatusOptionType>) => (
  <components.SingleValue {...props}>
    <div className="flex items-center gap-2">
      <div
        className={props.data.color}
        style={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          backgroundColor: props.data.color,
          flexShrink: 0,
          marginRight: "8px",
        }}
      />
      <span>{props.data.label}</span>
    </div>
  </components.SingleValue>
);

const statusOptions = [
  { label: "Present and paid", value: "PRESENT_AND_PAID", color: "green" },
  { label: "Present and unpaid", value: "PRESENT_NOT_PAID", color: "purple" },
  { label: "Missing", value: "MISSING", color: "red" },
  { label: "Requested", value: "REQUESTED", color: "yellow" },
] as const;

const FormErrors = ({ errors }: { errors: FieldErrors<PayHistoryPayload> }) => {
  if (Object.keys(errors).length === 0) return null;

  return (
    <div className="error-summary">
      {Object.entries(errors).map(([field, error]) => (
        <div key={field} className="error-message">
          {error?.message || `${field} is invalid`}
        </div>
      ))}
    </div>
  );
};

const PayHistoryModal = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initialData,
  title,
  service,
}: PayHistoryModalProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PayHistoryPayload & { source_document_id: number | null }>({
    defaultValues: {
      provider_name: "",
      description: "",
      date_of_service: "",
      date_of_service_end: "",
      amount: 0,
      status: "UNVERIFIED",
      source_document_id: null,
    },
    mode: "onBlur",
    shouldFocusError: false,
  });

  const { data: documentsData } = useGetDocumentsQuery(
    {
      fileId: service.file.id.toString(),
      limit: "1000",
      offset: "0",
      sortBy: "original_filename",
    },
    {
      skip: !service.file.id || !isOpen,
    },
  );

  const documents = documentsData?.results || [];

  const documentOptions: DocumentOption[] = documents.map((doc) => ({
    label: doc.original_filename,
    value: doc.id,
  }));

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        reset({
          provider_name: initialData.provider_name,
          description: initialData.description,
          date_of_service: initialData.date_of_service,
          date_of_service_end: initialData.date_of_service_end || "",
          amount: initialData.amount,
          status: initialData.status,
          source_document_id: initialData.source_document_id,
        });
      } else {
        reset({
          provider_name: "",
          description: "",
          date_of_service: "",
          date_of_service_end: "",
          amount: 0,
          status: "UNVERIFIED",
          source_document_id: null,
        });
      }
    }
  }, [isOpen, initialData, reset]);

  const handleFormSubmit = handleSubmit(async (data) => {
    try {
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
    }
  });

  const handleDelete = async () => {
    try {
      if (onDelete && initialData) {
        await onDelete(initialData.id.toString());
        setShowDeleteConfirm(false);
        onClose();
      }
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  return (
    <>
      <KaboodleModal
        isOpen={isOpen}
        onClose={onClose}
        footer={
          <>
            <div className="flex jc-c">
              <Button onClick={handleFormSubmit}>Save</Button>
              <Button onClick={onClose}>Cancel</Button>
            </div>
          </>
        }
      >
        <Card
          heading={title}
          headerButton={
            initialData && (
              <ButtonIcon
                kind="neutral"
                size="small"
                onClick={() => setShowDeleteConfirm(true)}
                className="delete"
              >
                delete
              </ButtonIcon>
            )
          }
        >
          <FormErrors errors={errors} />
          <Controller
            name="provider_name"
            control={control}
            rules={{ required: "Provider name is required" }}
            render={({ field }) => (
              <TextInput
                {...field}
                ref={field.ref}
                label="Provider Name"
                errors={errors}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            rules={{ required: "Description is required" }}
            render={({ field }) => (
              <TextInput
                {...field}
                ref={field.ref}
                label="Description"
                errors={errors}
              />
            )}
          />
          <Controller
            name="date_of_service"
            control={control}
            rules={{
              required: "Service start date is required",
              validate: (value, formValues) => {
                if (!value) return "Service start date is required";
                const date = new Date(value);
                if (isNaN(date.getTime())) return "Invalid date format";
                if (date > new Date()) return "Date cannot be in the future";
                if (formValues.date_of_service_end) {
                  const endDate = new Date(formValues.date_of_service_end);
                  if (!isNaN(endDate.getTime()) && date > endDate) {
                    return "Service start date cannot be after service end date";
                  }
                }
                return true;
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <DatePicker
                {...field}
                initialDate={value ? new Date(value) : undefined}
                onDateChange={(date) => {
                  onChange(date ? date.toISOString().split("T")[0] : "");
                }}
                label="Service Start Date"
                errors={errors}
              />
            )}
          />
          <Controller
            name="date_of_service_end"
            control={control}
            rules={{
              validate: (value, formValues) => {
                if (!value) return true;
                const date = new Date(value);
                if (isNaN(date.getTime())) return "Invalid date format";
                if (date > new Date()) return "Date cannot be in the future";
                if (formValues.date_of_service) {
                  const startDate = new Date(formValues.date_of_service);
                  if (!isNaN(startDate.getTime()) && date < startDate) {
                    return "Service end date cannot be earlier than service start date";
                  }
                }
                return true;
              },
            }}
            render={({ field: { onChange, value, ...field } }) => (
              <DatePicker
                {...field}
                initialDate={value ? new Date(value) : undefined}
                onDateChange={(date) => {
                  onChange(date ? date.toISOString().split("T")[0] : "");
                }}
                label="Service End Date"
                errors={errors}
              />
            )}
          />
          <Controller
            name="amount"
            control={control}
            rules={{ required: "Amount is required" }}
            render={({ field }) => (
              <MaskedInput
                {...field}
                ref={field.ref}
                label="Amount"
                mask="dollar"
                errors={errors}
              />
            )}
          />
          <Controller
            name="status"
            control={control}
            rules={{ required: "Status is required" }}
            render={({ field: { onChange, value, ...field } }) => (
              <Select
                {...field}
                label="Status"
                options={statusOptions}
                value={statusOptions.find((option) => option.value === value)}
                errors={errors}
                components={{
                  Option: StatusOption,
                  SingleValue: StatusSingleValue,
                }}
                onChange={(option: (typeof statusOptions)[number]) => {
                  onChange(option.value);
                }}
                isClearable={false}
                isSearchable={false}
              />
            )}
          />
          <Controller
            name="source_document_id"
            control={control}
            render={({ field: { onChange, value, ...field } }) => {
              return (
                <Select
                  {...field}
                  label="Document"
                  options={documentOptions}
                  value={
                    documentOptions.find((option) => option.value === value) ||
                    null
                  }
                  errors={errors}
                  components={{
                    Option: DocumentOption,
                    SingleValue: DocumentSingleValue,
                  }}
                  onChange={(option) => {
                    onChange(option ? (option as DocumentOption).value : null);
                  }}
                  isClearable
                  isSearchable={false}
                />
              );
            }}
          />
        </Card>
      </KaboodleModal>

      {showDeleteConfirm && (
        <KaboodleModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          footer={
            <div className="flex jc-c">
              <Button className="delete" onClick={handleDelete}>
                Confirm Delete
              </Button>
              <Button onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
            </div>
          }
        >
          <Card heading="Delete Pay History">
            <p>Are you sure you want to delete this pay history item?</p>
          </Card>
        </KaboodleModal>
      )}
    </>
  );
};

export default PayHistoryModal;
